import { Button } from '@99designs/common/components/Button';
import { Icon } from '@99designs/common/components/Icon';
import { LoadableElement } from '@99designs/common/components/LoadableElement';
import { borders, boxShadows, breakpoints, colors, spaces } from '@99designs/common/core/settings';
import { typeScale } from '@99designs/common/core/settings/typography';
import { FormattedMessage } from '@99designs/common/i18n';
import { SquareOnlineStoreProjectType } from '__generated__/types';
import Heading from 'common/components/Heading';
import { Currency } from 'common/utils/currency';
import ContentSetting from 'projects/components/ContentSetting';
import Header from 'projects/components/Header';
import PartnerLogo from 'projects/components/PartnerLogo';
import Price from 'projects/components/Price';
import Box from 'projects/libs/core-components/Box/Box';
import Stack from 'projects/libs/core-components/Stack/Stack';
import { useCreateBrief } from 'projects/mutations/CreateBrief';
import { Partner } from 'projects/PartnerContext';
import { BriefFieldsRedirect } from 'projects/routes';
import React from 'react';
import styled from 'styled-components';
import useGetPackage from './GetPackage';
import { projectTypes } from './projectTypes';

const [, maxWidthSmall] = breakpoints.small;
const [, maxWidthMedium] = breakpoints.medium;

const Container = styled.div`
    display: grid;
    grid-template-columns: 2fr 1fr;
    grid-gap: ${spaces.gutter}px;

    @media screen and (max-width: ${maxWidthMedium}px) {
        grid-template-columns: 1fr 1fr;
    }

    @media screen and (max-width: ${maxWidthSmall}px) {
        grid-template-columns: 1fr;
    }
`;

const ProjectTypes = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: ${spaces.gutter}px;

    @media screen and (max-width: ${maxWidthMedium}px) {
        grid-template-columns: 1fr;
    }
`;

const Package = styled.div`
    @media screen and (max-width: ${maxWidthSmall}px) {
        // Render package up top on mobile
        grid-column: 1;
        grid-row: 1;
    }
`;

const Tile = styled.div`
    border-radius: ${borders.borderRadiuses.medium}px;
    box-shadow: ${boxShadows.medium};
    padding: ${spaces.halfGutter}px;
    display: grid;
    grid-gap: ${spaces.halfGutter}px;
    grid-template-rows: auto 1fr auto;
`;

const Color = styled.div`
    color: ${props => props.color};
`;

const StyledPackageItem = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    padding-bottom: ${spaces.halfGutter}px;
    padding-top: ${spaces.halfGutter}px;

    & + & {
        border-top: ${borders.content};
    }
`;

const PriceLabel = styled.span`
    font-size: ${typeScale.size4}px;
`;

const FixedPricePackage = styled.strong`
    color: ${colors.fiftyFirstShade};
`;

const PackageItem: React.FunctionComponent = ({ children }) => (
    <StyledPackageItem>
        <Color color={colors.semantic.success}>
            <Icon type="tick-1" />
        </Color>
        <Box marginLeft="halfGutter">{children}</Box>
    </StyledPackageItem>
);

export default function CreateSquareOnlineStore() {
    const [
        createBrief,
        { error: createBriefError, data: createBriefData, loading: createBriefLoading },
    ] = useCreateBrief();
    const {
        error: getPackageError,
        data: getPackageData,
        loading: getPackageLoading,
    } = useGetPackage({ variables: { key: 'square-online-store-ecommerce-v1' } });

    function createSquareOnlineStoreBrief(projectType: SquareOnlineStoreProjectType) {
        createBrief({ variables: { fields: { squareOnlineStore: { projectType } } } });
    }

    function createSquareOnlineStoreEcommerceBrief() {
        createBrief({ variables: { fields: { squareOnlineStoreEcommerce: {} } } });
    }

    if (createBriefError) {
        throw createBriefError;
    }

    if (getPackageError) {
        throw getPackageError;
    }

    if (createBriefLoading || getPackageLoading) {
        return <LoadableElement variations={['fade-in']} isLoading={true} />;
    }

    if (createBriefData) {
        return <BriefFieldsRedirect brief={createBriefData.createStructuredBrief} push={true} />;
    }

    if (!getPackageData) {
        throw new Error('package not found');
    }

    const { package: projectPackage } = getPackageData;

    return (
        <ContentSetting>
            <Header title={__('What best describes your need?')}>
                <Stack space="space8">
                    <Heading size="h2" margin="no">
                        {__('What best describes your need?')}
                    </Heading>
                    <PartnerLogo partner={Partner.Square} />
                    <Color color={colors.fiftyFirstShade}>
                        {__(
                            'Choose the level of expert support you need for your Square Online Store'
                        )}
                    </Color>
                </Stack>
            </Header>

            <Container>
                <ProjectTypes>
                    {Object.keys(projectTypes).map((key: SquareOnlineStoreProjectType) => (
                        <Tile data-testid={`projectType:${key}`}>
                            <Stack space="quarterGutter">
                                <Heading size="h4" margin="no">
                                    {projectTypes[key].title}
                                </Heading>
                                <PriceLabel>
                                    <FormattedMessage
                                        message={__('from {price}')}
                                        values={{
                                            price: (
                                                <Price
                                                    amount={projectTypes[key].price}
                                                    currency={Currency.USD}
                                                />
                                            ),
                                        }}
                                    />
                                </PriceLabel>
                            </Stack>
                            <div>{projectTypes[key].description}</div>
                            <Button
                                variations={['primary', 'fluid', 'jumbo']}
                                onClick={() => createSquareOnlineStoreBrief(key)}
                            >
                                {__('Get started')}
                            </Button>
                        </Tile>
                    ))}
                </ProjectTypes>
                <Package>
                    <Tile data-testid={`package:${projectPackage.key}`}>
                        <Stack space="quarterGutter">
                            <Color color="#394c36">
                                <Heading size="h4" margin="no">
                                    {projectPackage.view.title}
                                </Heading>
                            </Color>
                            <PriceLabel>
                                <Price
                                    amount={
                                        projectPackage.price.fees + projectPackage.price.earnings
                                    }
                                    currency={projectPackage.price.currency as Currency}
                                />
                            </PriceLabel>
                            <FixedPricePackage>{__('Fixed price package')}</FixedPricePackage>
                        </Stack>
                        <Stack space="quarterGutter">
                            <div>{projectPackage.view.description}</div>
                            <div>
                                {projectPackage.view.conditions.map(item => (
                                    <PackageItem key={item}>{item}</PackageItem>
                                ))}
                            </div>
                        </Stack>
                        <Color color="#394c36">
                            <Button
                                variations={['primary', 'fluid', 'jumbo', 'chameleon']}
                                onClick={() => createSquareOnlineStoreEcommerceBrief()}
                            >
                                {__('Get started')}
                            </Button>
                        </Color>
                    </Tile>
                </Package>
            </Container>
        </ContentSetting>
    );
}
